import React, { useState, useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { resetPasswordValidation, calculateStrength } from 'validation/auth.validation';
import MobileContext from 'context/MobileContext';
import Button from 'components/shared/Button';
import { Dialog, makeStyles } from '@material-ui/core';
import { checkmarkCircleOutline, hidePassword, showPassword } from 'assets/newIcons';
import history from 'store/history';
import Text from 'components/shared/Text';
import styles from './style.module.css';
import { CircularProgress } from '@mui/material';

const initialValues = {
  password: '',
  confirmPassword: '',
};

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    borderRadius: '20px',
  },
}));

const SetPassword = ({
  changePassword,
  isSettingPassword,
  setPasswordData,
  resetPasswordData,
}) => {
  const isMobile = useContext(MobileContext);
  const [openThanksModal, setOpenThanksModal] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(-1); // -1 means no input yet
  const classes = useStyles();
  const hubspotId = useSelector(state => state?.auth?.user?.sfId);

  useEffect(() => {
    if (Object.keys(setPasswordData).length > 0) {
      setOpenThanksModal(true);
    }
  }, [setPasswordData]);

  // Clear password data when component unmounts
  useEffect(() => {
    return () => {
      resetPasswordData();
    };
  }, [resetPasswordData]);

  const passwordRequirements = [
    'Your password must be a minimum of 6 characters long.',
    'We suggest it contains at least one capital letter, one number, and one special character (?!@#$%^--&*()_+`~).',
  ];

  const handleGoToBackClick = () => {
    setOpenThanksModal(false);
    resetPasswordData();
    history.push('/');
  };

  const handlePasswordChange = (e, handleChange) => {
    const { value } = e.target;
    handleChange(e);

    const displayStrength = calculateStrength(value);
    setPasswordStrength(displayStrength);
  };

  return (
    <div className={styles.page}>
      {!isMobile ? <p className={styles.header}>SET NEW PASSWORD</p> : ''}

      <Formik
        initialValues={initialValues}
        validationSchema={resetPasswordValidation}
        onSubmit={values => {
          if (hubspotId) {
            changePassword({
              password: values.password,
              hubspotId,
            });
          }
        }}
      >
        {({
          handleSubmit,
          handleChange,
          values,
          errors,
          touched,
          handleBlur,
          isValid,
          dirty,
        }) => (
          <form className={styles.form} onSubmit={handleSubmit}>
            <div className={styles.fieldContainer}>
              <label className={styles.label}>Password</label>
              <div className={styles.passwordField}>
                <input
                  type={showPass ? 'text' : 'password'}
                  onChange={e => handlePasswordChange(e, handleChange)}
                  value={values.password}
                  name="password"
                  placeholder=""
                  onBlur={handleBlur}
                  disabled={isSettingPassword}
                  className={styles.passwordInput}
                />
                <img
                  src={showPass ? hidePassword : showPassword}
                  alt="toggle password"
                  onClick={() => setShowPass(!showPass)}
                  style={{ cursor: 'pointer' }}
                />
              </div>

              <div className={styles.passwordStrength}>
                <span className={styles.strengthText}>PASSWORD STRENGTH:</span>
                {passwordStrength >= 0 && (
                  <span
                    className={`${styles.strengthIndicatorText} ${
                      passwordStrength === 0 || passwordStrength === 1
                        ? styles.weakText
                        : passwordStrength === 4
                        ? styles.strongText
                        : styles.mediumText
                    }`}
                  >
                    {passwordStrength === 0 || passwordStrength === 1
                      ? 'WEAK'
                      : passwordStrength === 4
                      ? 'STRONG'
                      : 'MEDIUM'}
                  </span>
                )}
                <div className={styles.strengthDots}>
                  <div
                    className={`${styles.dot} ${
                      passwordStrength === 0 || passwordStrength === 1
                        ? styles.dotWeak
                        : passwordStrength === 4
                        ? styles.dotStrong
                        : passwordStrength >= 1
                        ? styles.dotActive
                        : ''
                    }`}
                  ></div>
                  <div
                    className={`${styles.dot} ${
                      passwordStrength === 4
                        ? styles.dotStrong
                        : passwordStrength >= 2
                        ? styles.dotActive
                        : ''
                    }`}
                  ></div>
                  <div
                    className={`${styles.dot} ${
                      passwordStrength === 4
                        ? styles.dotStrong
                        : passwordStrength >= 3
                        ? styles.dotActive
                        : ''
                    }`}
                  ></div>
                  <div
                    className={`${styles.dot} ${
                      passwordStrength === 4 ? styles.dotStrong : ''
                    }`}
                  ></div>
                </div>
              </div>
              {touched.password && errors.password && (
                <p className={styles.error}>{errors.password}</p>
              )}
            </div>

            <div className={styles.fieldContainer}>
              <label className={styles.label}>Confirm Password</label>
              <div className={styles.passwordField}>
                <input
                  type={showConfirmPass ? 'text' : 'password'}
                  onChange={handleChange}
                  value={values.confirmPassword}
                  name="confirmPassword"
                  placeholder=""
                  onBlur={handleBlur}
                  disabled={isSettingPassword}
                  className={styles.passwordInput}
                />
                <img
                  src={showConfirmPass ? hidePassword : showPassword}
                  alt="toggle password"
                  onClick={() => setShowConfirmPass(!showConfirmPass)}
                  style={{ cursor: 'pointer' }}
                />
              </div>
              {touched.confirmPassword && errors.confirmPassword && (
                <p className={styles.error}>{errors.confirmPassword}</p>
              )}
            </div>

            <div className={styles.passwordInfo}>
              {passwordRequirements.map((requirement, index) => (
                <div key={index} className={styles.passwordInfoItem}>
                  {requirement}
                </div>
              ))}
            </div>

            <div className={isMobile ? styles.buttonContainer : ''}>
              <Button
                className={styles.saveButton}
                type="submit"
                disabled={isSettingPassword || !(isValid && dirty)}
              >
                {isSettingPassword ? (
                  <CircularProgress size={20} style={{ color: '#fe5000' }} />
                ) : isMobile ? (
                  'Submit'
                ) : (
                  'Save'
                )}
              </Button>
            </div>
          </form>
        )}
      </Formik>
      <Dialog open={openThanksModal} classes={{ paper: classes.dialogPaper }}>
        <div className={styles.iconWrapper}>
          <img
            alt=""
            src={checkmarkCircleOutline}
            className={`${styles.checkLogo} ${isMobile ? styles.mob : ''}`}
          />
          <Text className={styles.thanksText2}>Your password has been updated.</Text>
          <p onClick={handleGoToBackClick} className={styles.goBackText}>
            Go Back to Home
          </p>
        </div>
      </Dialog>
    </div>
  );
};

SetPassword.propTypes = {
  changePassword: PropTypes.func.isRequired,
  isSettingPassword: PropTypes.bool,
  setPasswordData: PropTypes.object,
  resetPasswordData: PropTypes.func.isRequired,
};

SetPassword.defaultProps = {
  isSettingPassword: false,
  setPasswordData: {},
};

export default SetPassword;
